import { REFRESH_TOKEN_HEADER } from '@recipes/contracts';
import { HttpClient } from './http.js';
/**
 * Resource-oriented API surface. Every method maps 1:1 onto an endpoint documented in
 * the OpenAPI spec; paths are relative to /api/v1.
 */
export function createApiClient(options) {
    const http = new HttpClient(options);
    const withKey = (idempotencyKey) => idempotencyKey ? { idempotencyKey } : undefined;
    return {
        http,
        config: () => http.get('/api/v1/config'),
        auth: {
            register: (body) => http.post('/auth/register', body, { noRetry: true }),
            login: (body) => http.post('/auth/login', body, { noRetry: true }),
            /** `refreshToken` is only passed by native shells; browsers rely on the HttpOnly cookie. */
            refresh: (refreshToken) => http.post('/auth/refresh', undefined, {
                noRetry: true,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    ...(refreshToken ? { [REFRESH_TOKEN_HEADER]: refreshToken } : {}),
                },
            }),
            logout: (refreshToken) => http.post('/auth/logout', undefined, {
                noRetry: true,
                headers: refreshToken ? { [REFRESH_TOKEN_HEADER]: refreshToken } : undefined,
            }),
            me: () => http.get('/auth/me'),
            /** Native shell, AUTH_MODE=oidc: the one-time code from the deep link becomes a session. */
            exchangeOidcCode: (body) => http.post('/auth/oidc/exchange', body, { noRetry: true }),
            verifyEmail: (body) => http.post('/auth/verify-email', body, { noRetry: true }),
            resendVerification: () => http.post('/auth/resend-verification'),
            /** From the login page of an unconfirmed account; answers ok whatever the address is. */
            resendVerificationEmail: (body) => http.post('/auth/resend-verification-email', body),
            /** A new link for the holder of an old (expired) one; works without a session. */
            resendVerificationLink: (body) => http.post('/auth/resend-verification-link', body),
            forgotPassword: (body) => http.post('/auth/forgot-password', body, { noRetry: true }),
            resetPassword: (body) => http.post('/auth/reset-password', body, { noRetry: true }),
            changePassword: (body) => http.post('/auth/change-password', body),
            sessions: () => http.get('/auth/sessions'),
            revokeSession: (id) => http.delete(`/auth/sessions/${id}`),
            revokeOtherSessions: () => http.delete('/auth/sessions'),
        },
        users: {
            profile: () => http.get('/users/me'),
            updateProfile: (body) => http.patch('/users/me', body),
            deleteAccount: (body) => http.delete('/users/me', body),
        },
        recipes: {
            list: (query = {}) => http.get('/recipes', { query: query }),
            get: (id) => http.get(`/recipes/${id}`),
            create: (body, idempotencyKey) => http.post('/recipes', body, withKey(idempotencyKey)),
            update: (id, body) => http.patch(`/recipes/${id}`, body),
            remove: (id) => http.delete(`/recipes/${id}`),
            restore: (id) => http.post(`/recipes/${id}/restore`),
            purge: (id) => http.delete(`/recipes/${id}/purge`),
            duplicate: (id, body = { asVariation: false }) => http.post(`/recipes/${id}/duplicate`, body),
            scalePreview: (id, body) => http.post(`/recipes/${id}/scale-preview`, body),
            revisions: (id) => http.get(`/recipes/${id}/revisions`),
            revision: (id, revision) => http.get(`/recipes/${id}/revisions/${revision}`),
            restoreRevision: (id, revision) => http.post(`/recipes/${id}/revisions/${revision}/restore`),
            variations: (id) => http.get(`/recipes/${id}/variations`),
            setFavorite: (id, favorite) => http.put(`/recipes/${id}/favorite`, { favorite }),
            share: (id) => http.get(`/recipes/${id}/share`),
            updateShare: (id, body) => http.put(`/recipes/${id}/share`, body),
            public: (slug) => http.get(`/public/recipes/${slug}`),
            suggestTags: (id) => http.post(`/recipes/${id}/suggest-tags`),
            report: (recipeId, reason) => http.post('/abuse-reports', { recipeId, reason }),
        },
        ratings: {
            get: (recipeId) => http.get(`/recipes/${recipeId}/rating`),
            set: (recipeId, body) => http.put(`/recipes/${recipeId}/rating`, body),
            cookingLog: (query = {}) => http.get('/cooking-log', { query }),
            recipeCookingLog: (recipeId, query = {}) => http.get(`/recipes/${recipeId}/cooking-log`, { query }),
            addCookingLog: (recipeId, body) => http.post(`/recipes/${recipeId}/cooking-log`, body),
            updateCookingLog: (id, body) => http.patch(`/cooking-log/${id}`, body),
            deleteCookingLog: (id) => http.delete(`/cooking-log/${id}`),
        },
        taxonomy: {
            categories: () => http.get('/categories'),
            tags: (query = {}) => http.get('/tags', { query }),
            createTag: (body) => http.post('/tags', body),
            renameTag: (id, body) => http.patch(`/tags/${id}`, body),
            /** Rename a tag and/or give it one of the ten category hues. */
            updateTag: (id, body) => http.patch(`/tags/${id}`, body),
            deleteTag: (id) => http.delete(`/tags/${id}`),
            mergeTags: (body) => http.post('/tags/merge', body),
        },
        units: {
            list: () => http.get('/units'),
            convert: (body) => http.post('/units/convert', body),
        },
        search: {
            suggest: (q, limit = 8) => http.get('/search/suggest', { query: { q, limit } }),
            facets: (query = {}) => http.get('/search/facets', { query }),
        },
        media: {
            createUploadIntent: (body) => http.post('/media/upload-intents', body),
            /** Keep a short-lived picture (e.g. a scanned cookbook page) as a recipe picture. */
            copy: (assetId, body = { purpose: 'RECIPE' }) => http.post(`/media/${assetId}/copy`, body),
            /** A picture from the web (a video's still) as a recipe picture; the server fetches it. */
            importUrl: (body) => http.post('/media/import-url', body),
            complete: (assetId, body = {}) => http.post(`/media/${assetId}/complete`, body),
            get: (assetId) => http.get(`/media/${assetId}`),
            transform: (assetId, body) => http.post(`/media/${assetId}/transform`, body),
            remove: (assetId) => http.delete(`/media/${assetId}`),
            /** Upload the file bytes to the signed URL from an upload intent. */
            upload: async (intent, file, signal) => {
                const doFetch = options.fetch ?? fetch;
                const res = await doFetch(intent.uploadUrl, {
                    method: intent.method,
                    headers: intent.headers,
                    body: file,
                    signal,
                });
                if (!res.ok)
                    throw new Error(`Upload failed with status ${res.status}`);
            },
        },
        collections: {
            list: (query = {}) => http.get('/collections', { query }),
            get: (id) => http.get(`/collections/${id}`),
            create: (body) => http.post('/collections', body),
            update: (id, body) => http.patch(`/collections/${id}`, body),
            remove: (id) => http.delete(`/collections/${id}`),
            addRecipes: (id, body) => http.post(`/collections/${id}/recipes`, body),
            removeRecipes: (id, body) => http.delete(`/collections/${id}/recipes`, body),
            reorder: (id, recipeIds) => http.put(`/collections/${id}/recipes/order`, { recipeIds }),
        },
        imports: {
            fromUrl: (body, idempotencyKey) => http.post('/imports/url', body, withKey(idempotencyKey)),
            get: (jobId) => http.get(`/imports/${jobId}`),
            list: (query = {}) => http.get('/imports', { query }),
            confirm: (jobId, body) => http.post(`/imports/${jobId}/confirm`, body),
            cancel: (jobId) => http.post(`/imports/${jobId}/cancel`),
            ownJson: (body) => http.post('/imports/json', body),
            eventsUrl: (jobId) => http.url(`/imports/${jobId}/events`),
        },
        ai: {
            recipeFromText: (body, idempotencyKey) => http.post('/ai/recipe-from-text', body, withKey(idempotencyKey)),
            recipeFromImages: (body, idempotencyKey) => http.post('/ai/recipe-from-images', body, withKey(idempotencyKey)),
            recipeFromVideo: (body, idempotencyKey) => http.post('/ai/recipe-from-video', body, withKey(idempotencyKey)),
            sourceSearch: (body, idempotencyKey) => http.post('/ai/source-search', body, withKey(idempotencyKey)),
            generation: (id) => http.get(`/ai/generations/${id}`),
            generations: (query = {}) => http.get('/ai/generations', { query }),
            generationEventsUrl: (id) => http.url(`/ai/generations/${id}/events`),
            feedback: (id, body) => http.post(`/ai/generations/${id}/feedback`, body),
            cancel: (id) => http.post(`/ai/generations/${id}/cancel`),
            conversations: (query = {}) => http.get('/ai/conversations', { query }),
            createConversation: (body) => http.post('/ai/conversations', body),
            conversation: (id) => http.get(`/ai/conversations/${id}`),
            deleteConversation: (id) => http.delete(`/ai/conversations/${id}`),
            sendMessage: (id, body) => http.post(`/ai/conversations/${id}/messages`, body),
            applyProposal: (id, body) => http.post(`/ai/conversations/${id}/apply`, body),
            cookingQuestion: (body) => http.post('/ai/cooking-question', body),
            mealSuggestions: (body) => http.post('/ai/meal-suggestions', body),
        },
        /** The AI chat (docs/ai-chat.md): the agent in the ai-chat container. */
        aiChat: {
            status: () => http.get('/ai-chat/status'),
            conversations: (query = {}) => http.get('/ai-chat/conversations', { query }),
            createConversation: (body = {}) => http.post('/ai-chat/conversations', body),
            conversation: (id) => http.get(`/ai-chat/conversations/${id}`),
            renameConversation: (id, body) => http.patch(`/ai-chat/conversations/${id}`, body),
            deleteConversation: (id) => http.delete(`/ai-chat/conversations/${id}`),
            sendMessage: (id, body) => http.post(`/ai-chat/conversations/${id}/messages`, body),
            compact: (id) => http.post(`/ai-chat/conversations/${id}/compact`),
            interrupt: (id) => http.post(`/ai-chat/conversations/${id}/interrupt`),
            answerAsk: (id, askId, body) => http.post(`/ai-chat/conversations/${id}/asks/${encodeURIComponent(askId)}`, body),
            eventsUrl: (id) => http.url(`/ai-chat/conversations/${id}/events`),
        },
        speech: {
            transcribe: (body) => http.post('/speech/transcriptions', body),
        },
        timers: {
            list: () => http.get('/timers'),
            create: (body) => http.post('/timers', body),
            act: (id, body) => http.post(`/timers/${id}/actions`, body),
            remove: (id) => http.delete(`/timers/${id}`),
        },
        households: {
            list: () => http.get('/households'),
            get: (id) => http.get(`/households/${id}`),
            create: (body) => http.post('/households', body),
            update: (id, body) => http.patch(`/households/${id}`, body),
            remove: (id) => http.delete(`/households/${id}`),
            invite: (id, body) => http.post(`/households/${id}/invitations`, body),
            revokeInvitation: (id, invitationId) => http.delete(`/households/${id}/invitations/${invitationId}`),
            previewInvitation: (token) => http.get('/households/invitations/preview', { query: { token } }),
            acceptInvitation: (body) => http.post('/households/invitations/accept', body),
            declineInvitation: (body) => http.post('/households/invitations/decline', body),
            updateMemberRole: (id, userId, body) => http.patch(`/households/${id}/members/${userId}`, body),
            removeMember: (id, userId) => http.delete(`/households/${id}/members/${userId}`),
            leave: (id) => http.post(`/households/${id}/leave`),
            transferOwnership: (id, body) => http.post(`/households/${id}/transfer-ownership`, body),
        },
        mealPlan: {
            week: (query) => http.get('/meal-plan', { query }),
            create: (body) => http.post('/meal-plan/entries', body),
            update: (id, body) => http.patch(`/meal-plan/entries/${id}`, body),
            remove: (id) => http.delete(`/meal-plan/entries/${id}`),
            toShoppingList: (body) => http.post('/meal-plan/shopping-list', body),
        },
        shopping: {
            list: (query = {}) => http.get('/shopping-lists', { query }),
            get: (id) => http.get(`/shopping-lists/${id}`),
            create: (body) => http.post('/shopping-lists', body),
            update: (id, body) => http.patch(`/shopping-lists/${id}`, body),
            remove: (id) => http.delete(`/shopping-lists/${id}`),
            addItem: (id, body) => http.post(`/shopping-lists/${id}/items`, body),
            updateItem: (id, itemId, body) => http.patch(`/shopping-lists/${id}/items/${itemId}`, body),
            removeItem: (id, itemId) => http.delete(`/shopping-lists/${id}/items/${itemId}`),
            /** "Remind me": one reminder per list and per item; set again to change it. */
            setReminder: (id, body) => http.put(`/shopping-lists/${id}/reminder`, body),
            removeReminder: (id) => http.delete(`/shopping-lists/${id}/reminder`),
            setItemReminder: (id, itemId, body) => http.put(`/shopping-lists/${id}/items/${itemId}/reminder`, body),
            removeItemReminder: (id, itemId) => http.delete(`/shopping-lists/${id}/items/${itemId}/reminder`),
            addRecipe: (id, body) => http.post(`/shopping-lists/${id}/recipes`, body),
            bulk: (id, body) => http.post(`/shopping-lists/${id}/bulk`, body),
            /** Several items at once (voice, pasted lines); answers the outcome per item. */
            addItems: (id, body, idempotencyKey) => http.post(`/shopping-lists/${id}/items/batch`, body, {
                idempotencyKey,
            }),
            /** Device copy: everything without `since`, the delta with it. */
            sync: (since) => http.get('/shopping-lists/sync', { query: { since } }),
            suggestions: (listId, q) => http.get('/shopping-lists/suggestions', { query: { listId, q } }),
            hideSuggestion: (historyId) => http.delete(`/shopping-lists/history/${historyId}`),
            parseUtterance: (body, signal) => http.post('/shopping-lists/parse-utterance', body, { signal }),
            /** Live changes of one list (SSE, see `streamSse`). */
            eventsUrl: (id) => http.url(`/shopping-lists/${id}/events`),
        },
        pantry: {
            list: (query = {}) => http.get('/pantry', { query }),
            create: (body) => http.post('/pantry', body),
            update: (id, body) => http.patch(`/pantry/${id}`, body),
            remove: (id) => http.delete(`/pantry/${id}`),
            match: (body) => http.post('/pantry/match', body),
        },
        /** Tasting notes ("Eaten at …"): venue snapshots, visits and their dishes. */
        /** Ingredient catalog (docs/finished/ingredient-catalog-spec.md). */
        ingredients: {
            list: (query = {}) => http.get('/ingredients', { query }),
            get: (id) => http.get(`/ingredients/${id}`),
            create: (body) => http.post('/ingredients', body),
            update: (id, body) => http.patch(`/ingredients/${id}`, body),
            addAlias: (id, body) => http.post(`/ingredients/${id}/aliases`, body),
            merge: (id, sourceIngredientId) => http.post(`/ingredients/${id}/merge`, { sourceIngredientId }),
            remove: (id) => http.delete(`/ingredients/${id}`),
            resolve: (names, householdId) => http.get('/ingredients/resolve', {
                query: { names: names.join(','), householdId },
            }),
            unlinked: () => http.get('/ingredients/unlinked'),
        },
        products: {
            list: (query = {}) => http.get('/products', { query }),
            get: (id) => http.get(`/products/${id}`),
            byBarcode: (gtin, householdId) => http.get(`/products/by-barcode/${encodeURIComponent(gtin)}`, {
                query: { householdId },
            }),
            create: (body, idempotencyKey) => http.post('/products', body, withKey(idempotencyKey)),
            update: (id, body) => http.patch(`/products/${id}`, body),
            merge: (id, sourceProductId) => http.post(`/products/${id}/merge`, { sourceProductId }),
            remove: (id) => http.delete(`/products/${id}`),
            labelScan: (id, body, idempotencyKey) => http.post(id ? `/products/${id}/label-scan` : '/products/label-scan', body, withKey(idempotencyKey)),
        },
        stores: {
            list: (query = {}) => http.get('/stores', { query }),
            get: (id) => http.get(`/stores/${id}`),
            create: (body) => http.post('/stores', body),
            update: (id, body) => http.patch(`/stores/${id}`, body),
            merge: (id, sourceStoreId) => http.post(`/stores/${id}/merge`, { sourceStoreId }),
            remove: (id, hints = 'keep') => http.delete(`/stores/${id}`, undefined, { query: { hints } }),
        },
        loyaltyCards: {
            list: () => http.get('/loyalty-cards'),
            get: (id) => http.get(`/loyalty-cards/${id}`),
            create: (body) => http.post('/loyalty-cards', body),
            update: (id, body) => http.patch(`/loyalty-cards/${id}`, body),
            remove: (id) => http.delete(`/loyalty-cards/${id}`),
        },
        purchaseHints: {
            list: (query = {}) => http.get('/purchase-hints', { query }),
            create: (body, idempotencyKey) => http.post('/purchase-hints', body, withKey(idempotencyKey)),
            update: (id, body) => http.patch(`/purchase-hints/${id}`, body),
            remove: (id) => http.delete(`/purchase-hints/${id}`),
        },
        /** Prices, the estimate of a list and price changes (shopping-in-store-spec 9). */
        prices: {
            list: (query = {}) => http.get('/prices', { query }),
            create: (body, idempotencyKey) => http.post('/prices', body, withKey(idempotencyKey)),
            update: (id, body) => http.patch(`/prices/${id}`, body),
            remove: (id) => http.delete(`/prices/${id}`),
            forProduct: (productId) => http.get(`/products/${productId}/prices`),
            history: (productId, query = {}) => http.get(`/products/${productId}/price-history`, { query }),
            changes: (query = {}) => http.get('/prices/changes', {
                query,
            }),
            storeIndex: (storeId, months) => http.get(`/stores/${storeId}/price-index`, { query: { months } }),
            estimate: (listId, storeId) => http.get(`/shopping-lists/${listId}/estimate`, { query: { storeId } }),
            compare: (listId, storeId) => http.get(`/shopping-lists/${listId}/estimate/compare`, {
                query: { storeId },
            }),
            /** Photo of a shelf price tag → a draft; nothing is saved by this call. */
            scanTag: (body) => http.post('/prices/scan-tag', body),
            inbox: () => http.get('/prices/inbox'),
            scan: (scanId) => http.get(`/prices/inbox/${scanId}`),
            dismissScan: (scanId) => http.delete(`/prices/inbox/${scanId}`),
        },
        receipts: {
            list: (query = {}) => http.get('/receipts', { query }),
            create: (body, idempotencyKey) => http.post('/receipts', body, withKey(idempotencyKey)),
            get: (id) => http.get(`/receipts/${id}`),
            update: (id, body) => http.patch(`/receipts/${id}`, body),
            updateLine: (id, lineId, body) => http.patch(`/receipts/${id}/lines/${lineId}`, body),
            confirm: (id, idempotencyKey) => http.post(`/receipts/${id}/confirm`, undefined, withKey(idempotencyKey)),
            deleteImages: (id) => http.delete(`/receipts/${id}/images`),
            remove: (id) => http.delete(`/receipts/${id}`),
        },
        catalog: {
            /** Delta sync for the device copy; pass the cursor of the previous call. */
            sync: (since) => http.get('/catalog/sync', { query: { since } }),
            shoppingListHints: (listId, storeId) => http.get(`/shopping-lists/${listId}/hints`, {
                query: { storeId },
            }),
            recipeLinks: (recipeId) => http.get(`/recipes/${recipeId}/ingredient-links`),
            setRecipeLink: (recipeId, recipeIngredientId, ingredientId) => http.put(`/recipes/${recipeId}/ingredients/${recipeIngredientId}/link`, { ingredientId }),
            recipeNutrition: (recipeId) => http.get(`/recipes/${recipeId}/nutrition`),
        },
        places: {
            list: (query = {}) => http.get('/places', { query }),
            get: (id) => http.get(`/places/${id}`),
            create: (body) => http.post('/places', body),
            update: (id, body) => http.patch(`/places/${id}`, body),
            merge: (id, sourcePlaceId) => http.post(`/places/${id}/merge`, { sourcePlaceId }),
            remove: (id, reassignTo) => http.delete(`/places/${id}`, undefined, { query: { reassignTo } }),
        },
        visits: {
            list: (query = {}) => http.get('/visits', { query }),
            map: (query = {}) => http.get('/visits/map', { query }),
            get: (id) => http.get(`/visits/${id}`),
            /** Pass a stable idempotency key so the offline queue can retry safely. */
            create: (body, idempotencyKey) => http.post('/visits', body, withKey(idempotencyKey)),
            update: (id, body) => http.patch(`/visits/${id}`, body),
            merge: (id, sourceVisitId) => http.post(`/visits/${id}/merge`, { sourceVisitId }),
            remove: (id) => http.delete(`/visits/${id}`),
            addNote: (id, body, idempotencyKey) => http.post(`/visits/${id}/notes`, body, withKey(idempotencyKey)),
            /** `noteIds` must list every dish of the visit exactly once. */
            reorderNotes: (id, body) => http.put(`/visits/${id}/notes/order`, body),
        },
        tastingNotes: {
            update: (id, body) => http.patch(`/tasting-notes/${id}`, body),
            /** Into another own visit, or `{ newVisit: true }` to split the dish off. */
            move: (id, destination) => http.post(`/tasting-notes/${id}/move`, destination),
            remove: (id) => http.delete(`/tasting-notes/${id}`),
            suggestRecipes: (id, body = {}, idempotencyKey) => http.post(`/tasting-notes/${id}/recipe-suggestions`, body, withKey(idempotencyKey)),
            linkRecipe: (id, body) => http.post(`/tasting-notes/${id}/recipes`, body),
            unlinkRecipe: (id, recipeId) => http.delete(`/tasting-notes/${id}/recipes/${recipeId}`),
            byRecipe: (recipeId) => http.get(`/tasting-notes/by-recipe/${recipeId}`),
        },
        notifications: {
            list: (query = {}) => http.get('/notifications', { query }),
            markRead: (id) => http.post(`/notifications/${id}/read`),
            markAllRead: () => http.post('/notifications/read-all'),
            subscribePush: (body) => http.post('/notifications/push-subscriptions', body),
            unsubscribePush: (endpoint) => http.delete('/notifications/push-subscriptions', { endpoint }),
        },
        dataExport: {
            create: (body) => http.post('/exports', body),
            list: () => http.get('/exports'),
            get: (id) => http.get(`/exports/${id}`),
        },
        admin: {
            users: (query = {}) => http.get('/admin/users', { query }),
            user: (id) => http.get(`/admin/users/${id}`),
            updateUser: (id, body) => http.patch(`/admin/users/${id}`, body),
            health: () => http.get('/admin/health'),
            jobs: (query = {}) => http.get('/admin/jobs', { query }),
            jobAction: (queue, jobId, action) => http.post(`/admin/jobs/${queue}/${jobId}`, { action }),
            featureFlags: () => http.get('/admin/feature-flags'),
            updateFeatureFlag: (key, body) => http.patch(`/admin/feature-flags/${key}`, body),
            nutritionProviders: () => http.get('/admin/nutrition-providers'),
            updateNutritionProvider: (key, body) => http.patch(`/admin/nutrition-providers/${key}`, body),
            testNutritionProvider: (key) => http.post(`/admin/nutrition-providers/${key}/test`),
            entitlements: (userId) => http.get(`/admin/users/${userId}/entitlements`),
            setEntitlements: (userId, keys) => http.put(`/admin/users/${userId}/entitlements`, { keys }),
            catalogBackfill: () => http.get('/admin/catalog/backfill'),
            startCatalogBackfill: () => http.post('/admin/catalog/backfill'),
            aiProviders: () => http.get('/admin/ai-providers'),
            updateAiProvider: (provider, body) => http.patch(`/admin/ai-providers/${provider}`, body),
            units: () => http.get('/admin/units'),
            createUnit: (body) => http.post('/admin/units', body),
            updateUnit: (id, body) => http.patch(`/admin/units/${id}`, body),
            densities: () => http.get('/admin/densities'),
            upsertDensity: (body) => http.put('/admin/densities', body),
            deleteDensity: (id) => http.delete(`/admin/densities/${id}`),
            createCategory: (body) => http.post('/admin/categories', body),
            updateCategory: (id, body) => http.patch(`/admin/categories/${id}`, body),
            deleteCategory: (id) => http.delete(`/admin/categories/${id}`),
            scraperFailures: (query = {}) => http.get('/admin/scraper/failures', { query }),
            scraperStats: () => http.get('/admin/scraper/stats'),
            domainRules: () => http.get('/admin/scraper/domain-rules'),
            createDomainRule: (body) => http.post('/admin/scraper/domain-rules', body),
            deleteDomainRule: (id) => http.delete(`/admin/scraper/domain-rules/${id}`),
            abuseReports: (query = {}) => http.get('/admin/abuse-reports', { query }),
            updateAbuseReport: (id, body) => http.patch(`/admin/abuse-reports/${id}`, body),
            auditLog: (query = {}) => http.get('/admin/audit-log', { query }),
            grantSupportAccess: (body) => http.post('/admin/support-access', body),
            supportGrants: () => http.get('/admin/support-access'),
            /** E-mail delivery and templates (docs/partial/email-and-support-spec.md 9). */
            mail: {
                status: () => http.get('/admin/mail/status'),
                test: (to) => http.post('/admin/mail/test', to ? { to } : {}),
                settings: () => http.get('/admin/mail/settings'),
                updateSettings: (body) => http.patch('/admin/mail/settings', body),
                templates: () => http.get('/admin/mail/templates'),
                template: (key, locale) => http.get(`/admin/mail/templates/${key}/${locale}`),
                saveTemplate: (key, locale, body) => http.put(`/admin/mail/templates/${key}/${locale}`, body),
                resetTemplate: (key, locale) => http.delete(`/admin/mail/templates/${key}/${locale}`),
                preview: (body, options) => http.post('/admin/mail/templates/preview', body, options),
                testTemplate: (key, locale, body) => http.post(`/admin/mail/templates/${key}/${locale}/test`, body),
                versions: (key, locale) => http.get(`/admin/mail/templates/${key}/${locale}/versions`),
                restoreVersion: (key, locale, version) => http.post(`/admin/mail/templates/${key}/${locale}/versions/${version}/restore`),
                messages: (query = {}) => http.get('/admin/mail/messages', { query }),
                suppressions: (query = {}) => http.get('/admin/mail/suppressions', { query }),
                liftSuppression: (id) => http.delete(`/admin/mail/suppressions/${id}`),
                webhookEvents: (query = {}) => http.get('/admin/mail/webhook-events', { query }),
                retryWebhookEvent: (id) => http.post(`/admin/mail/webhook-events/${id}/retry`),
            },
            /** The support inbox – roles ADMIN and SUPPORT. */
            support: {
                conversations: (query = {}) => http.get('/admin/support/conversations', { query }),
                conversation: (id) => http.get(`/admin/support/conversations/${id}`),
                update: (id, body) => http.patch(`/admin/support/conversations/${id}`, body),
                reply: (id, body) => http.post(`/admin/support/conversations/${id}/reply`, body),
                compose: (body) => http.post('/admin/support/conversations', body),
                markSpam: (id) => http.post(`/admin/support/conversations/${id}/spam`),
                remove: (id) => http.delete(`/admin/support/conversations/${id}`),
                html: (messageId, images) => http.get(`/admin/support/messages/${messageId}/html`, {
                    query: images ? { images: '1' } : {},
                }),
                attachment: (id) => http.get(`/admin/support/attachments/${id}`),
                unread: () => http.get('/admin/support/unread'),
                staff: () => http.get('/admin/support/staff'),
                settings: () => http.get('/admin/support/settings'),
                updateSettings: (body) => http.patch('/admin/support/settings', body),
                eventsUrl: () => http.url('/admin/support/events'),
            },
        },
    };
}
//# sourceMappingURL=client.js.map